import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Users, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const RepLoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(false);
  const [repInfo, setRepInfo] = useState(null);

  // Check if already logged in
  useEffect(() => {
    const token = localStorage.getItem('rep_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      navigate('/rep-dashboard');
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/reps/login`, { email, password });
      
      if (response.data.needs_password_setup) {
        setNeedsPasswordSetup(true);
        setRepInfo(response.data);
        toast.info('Please set up your password to continue');
      } else {
        // Save token and redirect
        localStorage.setItem('rep_token', response.data.access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
        toast.success('Login successful!');
        navigate('/rep-dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/reps/set-password`, { email, password });
      
      // Save token and redirect
      localStorage.setItem('rep_token', response.data.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      toast.success('Password set successfully! Welcome!');
      navigate('/rep-dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckEmail = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    try {
      // Try to login with empty password to check if password is set
      const response = await axios.post(`${API}/reps/login`, { email, password: '' });
      
      if (response.data.needs_password_setup) {
        setNeedsPasswordSetup(true);
        setRepInfo(response.data);
        toast.info(`Welcome ${response.data.name}! Please set up your password.`);
      }
    } catch (error) {
      // If error, password is already set or invalid email
      if (error.response?.status === 401) {
        // Rep exists, needs to enter password
        setNeedsPasswordSetup(false);
      } else {
        toast.error('Email not found. Please contact your exhibitor.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Home
        </Link>
        
        <Card>
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">Representative Login</CardTitle>
            <CardDescription>
              {needsPasswordSetup 
                ? `Welcome, ${repInfo?.name}! Set up your password to continue.`
                : 'Sign in to manage your booth'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {needsPasswordSetup ? (
              // Password Setup Form
              <form onSubmit={handleSetPassword} className="space-y-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={email}
                    disabled
                    className="bg-slate-50"
                  />
                </div>
                <div>
                  <Label>New Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your new password"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label>Confirm Password</Label>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Setting up...' : 'Set Password & Login'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full"
                  onClick={() => {
                    setNeedsPasswordSetup(false);
                    setRepInfo(null);
                    setPassword('');
                    setConfirmPassword('');
                  }}
                >
                  Use Different Email
                </Button>
              </form>
            ) : (
              // Login Form
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
                
                <div className="text-center">
                  <p className="text-sm text-slate-500">
                    First time? <button 
                      type="button"
                      onClick={handleCheckEmail}
                      className="text-primary hover:underline"
                    >
                      Check if you need to set up your password
                    </button>
                  </p>
                </div>
              </form>
            )}
            
            <div className="mt-6 pt-6 border-t border-slate-200 text-center text-sm text-slate-500">
              <p>Are you an exhibitor?</p>
              <Link to="/auth" className="text-primary hover:underline">
                Sign in to Exhibitor Dashboard
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RepLoginPage;
